/* -------------------------------------------------------------------------
 * dashboardAggregators.js — dashboard totals derived from the industry-
 * standard per-item valuation object (2026-02). Mirrors the exact spec:
 *
 *   1. Collection Value       = Σ current_value
 *   2. Total Cost             = Σ total_cost
 *   3. Realised Gains         = Σ realised_PL
 *   4. Unrealised Gains/Loss  = Σ unrealised_PL
 *   5. Total P/L              = Σ total_PL
 *   6. P/L %                  = Σ total_PL ÷ Σ total_cost × 100  (0 when total_cost is 0)
 *   7. Avg Bottle Value       = collectionValue ÷ remainingQty  — 2026-02-26:
 *                               numerator is VALUED items only (already gated
 *                               by includeInValuationTotals above), denominator
 *                               is ALL items (unconditional). This ensures the
 *                               average reflects the entire collection, not
 *                               just the subset with valuations set. Returns 0
 *                               when remainingQty is 0.
 *   8. Unrealised Loss        = |Σ unrealised_PL| when Σ unrealised_PL < 0, else 0
 *   9. Total Cost Value       = Σ itemCostNZD(it) — portfolio true cost basis,
 *                               unconditional (ignores includeInValuationTotals),
 *                               independent from valuation / market / P/L logic.
 *
 * Callers pass in an array of valuation objects (each produced by
 * `computeItemValuation`) and, optionally, the parallel array of raw items
 * so we can also derive the NZD cost basis. Any missing / null / NaN values
 * coerce to 0 via `_num` so the UI never renders `NaN` / `undefined` / `null`.
 * ------------------------------------------------------------------------- */

import { itemCostNZD } from "@/utils/itemValue";

const _num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

export function aggregateDashboardTotals(valuations = [], items = []) {
  let collectionValue = 0;
  let totalCost = 0;
  let realisedGains = 0;
  let unrealisedGains = 0;
  let totalPL = 0;
  let remainingQty = 0;
  let totalCostValue = 0;

  for (const v of valuations) {
    totalCost       += _num(v?.total_cost);
    remainingQty    += _num(v?.remaining_qty);
    if (v?.includeInValuationTotals === true) {
      collectionValue += _num(v?.current_value);
      realisedGains   += _num(v?.realised_PL);
      unrealisedGains += _num(v?.unrealised_PL);
      totalPL         += _num(v?.total_PL);
    }
  }

  // Portfolio true cost basis — every item contributes, regardless of
  // valuation status. Kept independent from the P/L accumulators above
  // so it can never be masked by a missing / inferred valuation.
  for (const it of items) {
    totalCostValue += _num(itemCostNZD(it));
  }

  const plPercent = totalCost === 0 ? 0 : (totalPL / totalCost) * 100;
  // Avg Bottle Value — total portfolio value (VALUED items only, per the
  // includeInValuationTotals gate on collectionValue above) divided by total
  // bottle count (ALL items, remainingQty is summed unconditionally). This
  // matches `avgBottleValue = totalValue / totalQuantity` from the spec so
  // the KPI reflects the entire collection rather than the valued subset.
  const avgBottleValue = remainingQty === 0 ? 0 : collectionValue / remainingQty;
  const unrealisedLoss = unrealisedGains < 0 ? -unrealisedGains : 0;

  return {
    collectionValue,
    totalCost,
    realisedGains,
    unrealisedGains,
    totalPL,
    plPercent,
    remainingQty,
    avgBottleValue,
    unrealisedLoss,
    totalCostValue,
  };
}
